
import React from 'react';
import { useTranslation } from 'react-i18next';
import { ReportData } from '@/types/report';
import { Badge } from '@/components/ui/badge';

type ClassInfoSectionProps = {
  report: ReportData;
  formatDate: (date: string | undefined) => string;
  formatTime: (date: string | undefined) => string;
};

export const ClassInfoSection: React.FC<ClassInfoSectionProps> = ({
  report,
  formatDate,
  formatTime,
}) => {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
      <div className="bg-gray-50 p-3 rounded-md">
        <p className="text-sm text-gray-500">{t('reports.classInfo.datetime')}</p>
        <p className="font-medium">
          {report.start_date ? (
            <>
              {formatDate(report.start_date)} {t('reports.classInfo.timeAt')} {formatTime(report.start_date)} - {formatTime(report.end_date || '')} {t('reports.classInfo.timeUnit')}
            </>
          ) : (
            <>
              {formatDate(report.teaching_date)} {t('reports.classInfo.timeAt')} {report.teaching_time_slot} {t('reports.classInfo.timeUnit')}
            </>
          )}
        </p>
      </div>
      <div className="bg-gray-50 p-3 rounded-md">
        <p className="text-sm text-gray-500">{t('reports.classInfo.duration')}</p>
        <p className="font-medium">{report.teaching_duration}</p>
      </div>
      
      {/* Display submission status if available */}
      {report.submitted_on_time !== undefined && (
        <div className="bg-gray-50 p-3 rounded-md col-span-full">
          <p className="text-sm text-gray-500">{t('reports.classInfo.submissionStatus')}</p>
          <div className="flex items-center gap-2">
            <Badge variant={report.submitted_on_time ? "success" : "destructive"}>
              {report.submitted_on_time ? t('reports.classInfo.onTime') : t('reports.classInfo.late')}
            </Badge>
            <p className="text-sm text-gray-600">
              {report.submitted_on_time 
                ? t('reports.classInfo.onTimeDesc')
                : t('reports.classInfo.lateDesc')}
            </p>
          </div>
        </div>
      )}
      
      {/* Display rejection reason if available */}
      {report.rejection_reason && (
        <div className="bg-red-50 border border-red-200 p-3 rounded-md col-span-full">
          <p className="text-sm text-red-700 font-semibold">{t('reports.classInfo.rejectionReason')}</p>
          <p className="text-red-600">{report.rejection_reason}</p>
        </div>
      )}
      
      {/* Display admin note if available */}
      {report.note && (
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-md col-span-full">
          <p className="text-sm text-blue-700 font-semibold">{t('reports.classInfo.adminNote')}</p>
          <p className="text-blue-600">{report.note}</p>
        </div>
      )}
    </div>
  );
};
